import { Table } from '@eigtech/ui-shared-data-grid'
import {
  Button,
  ButtonGroup,
  HStack,
  pluralize,
  Tag,
  TagCloseButton,
  TagLabel,
  useDisclosure,
} from '@eigtech/ui-shared-dave'
import {
  DocumentTagsModalProps,
  ExternalDocumentPublicationTargets,
  ExternalDocumentPublicationTargetsSchema,
} from '@eigtech/ui-shared-documents'
import { createHandle, DialogTrigger } from '@eigtech/ui/components/dialog'
import { startCase } from 'lodash-es'
import { FunctionComponent, RefObject } from 'react'
import {
  DocumentVisibilityDrawer,
  DocumentVisibilityDrawerBaseProps,
} from './DocumentVisibilityDrawer/DocumentVisibilityDrawer'
import { DocumentColumn } from './DocumentsTableColumns'
import {
  ExportDocumentDrawer,
  ExportDocumentDrawerBaseProps,
} from './ExportDocumentDrawer/ExportDocumentDrawer'

export type DocumentsBulkActionsProps = Omit<ExportDocumentDrawerBaseProps, 'documents'> &
  Omit<DocumentVisibilityDrawerBaseProps, 'documents'> & {
    numberOfSelectedRows: number
    resetRowSelection: () => void
    table: RefObject<Table<DocumentColumn>>
    DocumentTagsModal?: FunctionComponent<DocumentTagsModalProps>
    hidePublishField?: boolean
    hideVisibilityButton?: boolean
    excludedPublicationTargetReason?: string
  }

const tagsModalHandle = createHandle()

export function DocumentsBulkActions({
  numberOfSelectedRows,
  resetRowSelection,
  table,
  DocumentTagsModal,
  hidePublishField,
  hideVisibilityButton,
  ...props
}: DocumentsBulkActionsProps) {
  const getDocuments = () =>
    table.current?.getSelectedRowModel().flatRows.map(({ original }) => original) ?? []

  const exportDocumentDrawer = useDisclosure()
  const documentVisibilityDrawer = useDisclosure()

  const publicationTargets = [
    ...(props.additionalPublicationTargets ?? []).map((option) => option.value),
    ...ExternalDocumentPublicationTargetsSchema.options,
  ].filter(
    (target) =>
      !props.excludedPublicationTargets?.includes(target as ExternalDocumentPublicationTargets)
  )
  const hasPublicationTargets = !hidePublishField && !!publicationTargets.length

  if (!numberOfSelectedRows) return null

  return (
    <>
      <HStack>
        <Tag>
          <TagLabel>{numberOfSelectedRows} documents(s) selected</TagLabel>
          <TagCloseButton onClick={resetRowSelection} />
        </Tag>

        <ButtonGroup size="sm" spacing="1" variant="outline">
          {hasPublicationTargets && (
            <Button onClick={exportDocumentDrawer.onOpen}>
              Export {startCase(pluralize(props.documentType, numberOfSelectedRows))}
            </Button>
          )}

          {!hideVisibilityButton && (
            <Button onClick={documentVisibilityDrawer.onOpen}>
              Update {startCase(pluralize(props.documentType, numberOfSelectedRows))} Visibility
            </Button>
          )}

          {DocumentTagsModal && (
            <DialogTrigger handle={tagsModalHandle} render={<Button />}>
              Add Tags
            </DialogTrigger>
          )}
        </ButtonGroup>

        {DocumentTagsModal && (
          <DocumentTagsModal document={[...getDocuments()]} handle={tagsModalHandle} />
        )}
      </HStack>

      {exportDocumentDrawer.isOpen && (
        <ExportDocumentDrawer {...exportDocumentDrawer} {...props} documents={getDocuments} />
      )}
      {documentVisibilityDrawer.isOpen && (
        <DocumentVisibilityDrawer
          {...documentVisibilityDrawer}
          {...props}
          documents={getDocuments}
        />
      )}
    </>
  )
}
